import { cn } from '@/lib/utils';
import { type ComponentProps, type ReactNode, useId } from 'react';

interface ChampLoginProps extends Omit<ComponentProps<'input'>, 'id' | 'className'> {
  /** Visible label, above the field. */
  libelle: string;
  /** Marks the field as invalid for assistive technologies (`aria-invalid`). */
  invalide?: boolean;
  /** Control shown inside the frame, after the input (the show / hide button). */
  children?: ReactNode;
}

/**
 * A labelled field of the login form: 13 px label, 46 px frame with a 1 px `gray-500`
 * border (3.4:1: the `gray-300` of the board is 1.9:1 and does not show the field, S14 #5) and 8 px corners, 15 px input. The input is uncontrolled: the form reads it
 * through `FormData` at submit time. The frame carries the focus outline (3 px bleu nuit,
 * 2 px away, like everywhere else), since the input itself has none.
 */
export function ChampLogin({ libelle, invalide = false, children, ...props }: ChampLoginProps) {
  const id = useId();
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="font-semibold text-caption text-gray-800">
        {libelle}
      </label>
      <div
        className={cn(
          'flex h-[46px] items-center rounded-md border bg-white focus-within:border-black focus-within:outline-3 focus-within:outline-bleu-nuit focus-within:outline-offset-[-3px]',
          invalide ? 'border-black' : 'border-gray-500',
        )}
      >
        <input
          {...props}
          id={id}
          aria-invalid={invalide ? true : undefined}
          className="h-full min-w-0 flex-1 border-0 bg-transparent px-4 font-normal text-body text-gray-900 outline-none placeholder:font-light placeholder:text-gray-600"
        />
        {children}
      </div>
    </div>
  );
}
